import React, { Dispatch, FC, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { Action } from 'redux';

import lang from '../../lib/language';
import { EpicMoversParamType, EpicMoversTabEnum } from '../../lib/models/fundamental/types';
import { RootState } from '../../lib/store';
import { loadDiscoverChartData } from '../../lib/store/common-actions';
import { epicMoversFilterOption, setEpicMoversFilterOption } from '../../lib/store/discover/epicMoversState';
import { getEpicMovers as getEpicMoversCall } from '../../lib/store/fundamental/index';
import { epicMoversOptionToString, getEpicMoversOptionByStringValue } from '../../lib/store/fundamental/mappers';
import { FundamentalCall } from '../../lib/store/fundamental/types';
import { isCallStatusError, isCallStatusReady } from '../../lib/util/error-handling/StatusByCallHelpers';

import { Button } from '../../components';
import { ButtonTypesEnum } from '../../components/Button/Button.types';
import { MarketStateBadge } from '../../components/MarketStateBadge/MarketStateBadge';
import NavButtons from '../../components/NavigationButtons/NavButtons';
import NoDataAvailable from '../../components/NoDataAvailable/NoDataAvailable';
import { useIsMobile } from '../../hooks';
import { UI_ROUTES } from '../../util/routes';
import FilterDropDown from '../TopGainersAndLosers/components/FilterDropDown/FilterDropDown';

import EpicMoversList from './components/EpicMoversList/EpicMoversList';

import styles from './EpicMovers.module.scss';

const EpicMovers: FC<ReduxProps> = (props: ReduxProps): JSX.Element => {
  const { getEpicMovers, isEpicMoversReady, isEpicMoversError, epicMoversData } = props;
  const isRequestCompleted = isEpicMoversReady || isEpicMoversError;
  const { responseData } = epicMoversData;
  const { topGainers, topLosers } = responseData ?? {};
  const hasNoAnyData = isEmpty(topGainers) && isEmpty(topLosers) && isRequestCompleted;

  const history = useHistory();
  const isMobile = useIsMobile();

  const optionValue = epicMoversFilterOption.get();
  const [selectedTab, setSelectedTab] = useState<EpicMoversTabEnum>(EpicMoversTabEnum.TopGainers);
  const isTopGainersSelected = selectedTab === EpicMoversTabEnum.TopGainers;
  const isTopLoserSelected = selectedTab === EpicMoversTabEnum.TopLosers;

  const tabs = [
    {
      text: lang.commonEpicMoversGainers(),
      component: <></>,
    },
    {
      text: lang.commonEpicMoversLosers(),
      component: <></>,
    },
  ];

  const handleFilterOption = (option: number[], valueOption: string) => {
    const newOptionValue = getEpicMoversOptionByStringValue(valueOption);
    setEpicMoversFilterOption(newOptionValue);
    getEpicMovers({ range: newOptionValue });
  };

  useEffect(() => {
    getEpicMovers({ range: optionValue });
  }, [getEpicMovers, optionValue]);

  return (
    <div className={isMobile ? styles.wrapperMobile : styles.wrapper}>
      {isMobile ? (
        <div className={styles.wrapperTitleMob}>
          <h6 className={styles.title}>
            {lang.commonEpicMoversToday()}
            <span className={styles.marketStatus}><MarketStateBadge /></span>
          </h6>
          <div className={styles.tabsWrapper}>
            <div className={styles.tabs}>
              <NavButtons
                fullWidthButtons
                tabs={tabs}
                sendCurrentTabIndex={setSelectedTab}
                initialActiveTab={selectedTab}
              />
            </div>
            <FilterDropDown
              isDisabled={!isRequestCompleted}
              optionValue={epicMoversOptionToString(optionValue)}
              handleFilterOption={handleFilterOption}
              hasBackground
              hasExpandArrow
              isElliptic
              isEllipticAndMobile
            />
          </div>
        </div>
      ) : (
        <div className={styles.wrapperTitle}>
          <div className={styles.left}>
            <h6 className={styles.title}>
              {lang.commonEpicMoversToday()}
              <span className={styles.marketStatus}><MarketStateBadge /></span>
            </h6>
          </div>
          <div className={styles.right}>
            <div className={styles.filterWrapper}>
              <FilterDropDown
                isDisabled={!isRequestCompleted}
                optionValue={epicMoversOptionToString(optionValue)}
                handleFilterOption={handleFilterOption}
                hasBackground
                hasExpandArrow
                isElliptic
              />
            </div>
            <Button
              className={styles.viewAllBtn}
              disabled={!isRequestCompleted}
              variant={ButtonTypesEnum.Link}
              onClick={() => history.push({
                pathname: UI_ROUTES.discover + UI_ROUTES.epicMoversToday,
              })}
            >
              {lang.mobileFundsScreenViewAllBtn()}
            </Button>
          </div>
        </div>
      )}
      <div className={styles.listWrapper}>
        <div className={styles.list}>
          {
            hasNoAnyData && (
            <NoDataAvailable
              className={styles.noDataToDisplay}
              isNoDataIcon
              title={lang.commonDiscover52WeekNoDataAll()}
            />
            )
          }
          {!hasNoAnyData && (!isMobile || isTopGainersSelected)
            && (
            <EpicMoversList
              isReduction={false}
              isTopGainersSelected
              title={lang.commonEpicMoversGainers()}
              data={topGainers}
            />
            )}
          {!isMobile && <div className={styles.verticalSpace} />}
          {!hasNoAnyData && (!isMobile || isTopLoserSelected)
            && (
            <EpicMoversList
              isReduction
              isTopGainersSelected={false}
              title={lang.commonEpicMoversLosers()}
              data={topLosers}
            />
            )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isEpicMoversReady: isCallStatusReady(FundamentalCall.getEpicMovers, state.fundamental.statusByCall),
  isEpicMoversError: isCallStatusError(FundamentalCall.getEpicMovers, state.fundamental.statusByCall),
  epicMoversData: state.fundamental.epicMoversData,
  companyLogoAndName: state.fundamental.companyLogoAndName,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getChartData: (symbols: string) => dispatch(loadDiscoverChartData(symbols)),
  getEpicMovers: (payload: EpicMoversParamType) => dispatch(getEpicMoversCall(payload)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(EpicMovers);
